import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { BiEdit } from 'react-icons/bi';
import { FaCheckCircle, FaExclamationTriangle, FaIdCard, FaKey, FaSitemap, FaSyncAlt } from 'react-icons/fa';
import { FaUserLarge, FaUsersGear } from 'react-icons/fa6';
import { RiAdminLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { IsAdminUser, LabelConstants, UserType } from '../../Constants.ts';
import CreateButton from '../../UIComponents/CreateButton.tsx';
import DeleteRowButton from '../../UIComponents/DeleteRowButton.tsx';
import UserTypeBadge from '../../UIComponents/UserTypeBadge.tsx';
import { getADStatusUI } from '../../Utilities/UIUtils.tsx';
import { AreObjectsEquals, getMyLocalTime } from '../../Utilities/Utilities.ts';
import { AuditObjectTypes, UserModel } from '../../_core/_model.ts';
import { DeleteUser, GetUsers, ResetPassword } from '../../_core/_request.ts';
import { useAppContext } from '../../context/AppContext.tsx';
import { AuthContext } from '../../context/AuthContext.js';
import ComponentLayout from '../Layouts/ComponentLayout.tsx';
import SearchAndAdvancedFilters, { appliedFilterType } from '../Tables/SearchAndAdvancedFilters.tsx';
import TableLayoutEx from '../Tables/TableLayoutEx.tsx';
import TableSummary from '../Tables/TableSummary.tsx';
import UserWithDateCell from '../Tables/UserWithDateCell.tsx';
import TooltipButton from '../Tools/TooltipButton.tsx';
import AddAdUsersModal from './AddAdUsersModal.tsx';
import SyncAdUsersModal from './SyncAdUsersModal.tsx';
import { useToast } from '../../context/ToastContext.tsx';


export default function UserTable() {
  const [rows, setRows] = useState<UserModel[] | null>(null);
  const [rowSelection, setRowSelection] = React.useState<any[]>([]);
  const [isAdModalOpen, setIsAdModalOpen] = useState(false);
  const { showToast } = useToast();

  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  // --- Password Reset States ---
  const [resetUsers, setResetUsers] = useState<UserModel[] | null>(null);
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resetResult, setResetResult] = useState<{ successfulCount: number, failedCount: number } | null>(null);

  const { user, logout } = useContext(AuthContext);
  const { allUsers, setAllUsers, roles, allRoleGroups } = useAppContext();
  const navigate = useNavigate();
  const defaultFilter = { userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null, isAdUser: null };
  const [selectedFilter, setSelectedFilter] = useState<any>(defaultFilter);

  // Helper to extract selected UserModels regardless of TanStack state format
  const getSelectedUsers = (): UserModel[] => {
    if (Array.isArray(rowSelection)) return rowSelection;
    if (rows) {
      return Object.keys(rowSelection).map(key => rows[parseInt(key)]).filter(Boolean);
    }
    return [];
  };

  const handleResetClick = (targetUsers: UserModel[]) => {
    setResetUsers(targetUsers);
    setResetStatus('idle');
    setResetResult(null);
  };

  const confirmResetPassword = () => {
    if (!resetUsers || resetUsers.length === 0) return;
    setResetStatus('loading');

    const userIds = resetUsers.map(u => u.userID);

    ResetPassword({ UserIds: userIds })
      .then((res) => {
        setResetStatus('success');
        setResetResult(res.data);
        setRowSelection([]); // Clear selection upon success
      })
      .catch((err) => {
        console.error("Failed to reset passwords:", err);
        setResetStatus('error');
      });
  };

  const colDefinitions = {
    "Name": {
      accessorFn: (row) => getUserName(row),
      accessorKey: undefined,
      header: 'Name',
      enableSorting: true,
      enableResizing: true,
      cell: ({ cell, row }) => getUserName(row)
    },
    "Access": {
      accessorFn: (row) => UserType[row.applicationAccess],
      header: "App Access",
      size: 90,
      enableSorting: true,
      enableResizing: true,
      cell: ({ row }) => (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <UserTypeBadge noTitle={false} userType={row.original.applicationAccess} />
        </div>
      )
    },
    "Column Restrictions": {
      accessorKey: 'columnRestriction',
      header: 'Column Restrictions',
      enableSorting: true,
      enableResizing: true,
      cell: (cell) => (
        <TooltipButton title={cell.getValue()}>
          {
            cell.getValue()
          }
        </TooltipButton>
      )
    },
    "Email": {
      accessorKey: 'email',
      header: 'Email',
      enableSorting: true,
      enableResizing: true,
      cell: (cell) => cell.getValue()
    },
    "CreatedBy": {
      accessorKey: 'createdBy',
      size: 150,
      header: 'Created by',
      enableSorting: true,
      enableResizing: true,
      cell: ({ cell, row }) => (
        <UserWithDateCell {...{
          tDate: getMyLocalTime(row.original.createdAt),
          userId: cell.getValue()
        }} />
      )
    },
    "isAdUser": {
      accessorKey: 'isAdUser',
      header: 'AD Status',
      size: 135, // Slightly wider to accommodate text + icon
      enableResizing: false,
      enableSorting: true,
      sortFn: (rowA: any, rowB: any) => {
        const getSortWeight = (row: any) => {
          const isAd = row.original.isAdUser;
          const isEnabled = row.original.accountEnabled;

          if (isAd === 1 && isEnabled === true) return 3;  // Highest priority
          if (isAd === 1 && isEnabled === false) return 2; // Medium priority
          return 1;                                        // Lowest priority (Not AD)
        };

        // Compares the calculated weights to determine sort order
        return getSortWeight(rowA) - getSortWeight(rowB);
      },
      cell: ({ cell, row }) => {
        const adStatus = row.original.isAdUser;
        const accountEnabled = row.original.accountEnabled; // Fetch the new field
        const adInfo = getADStatusUI(adStatus, accountEnabled);

        return (
          <div className='d-flex flex-row align-items-center gap-2'>
            <TooltipButton title={adInfo.tooltip}>
              <span
                className="d-flex align-items-center gap-1"
                style={{
                  backgroundColor: adInfo.color,
                  color: adInfo.textColor,
                  border: `1px solid ${adInfo.border}`,
                  fontSize: '11px',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  cursor: 'help',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  lineHeight: '1.2'
                }}
              >
                {adInfo.icon}
                {adInfo.label}
              </span>
            </TooltipButton>
          </div>
        )
      }
    },
    "action": {
      accessorKey: 'action',
      header: ' ',
      size: 125,
      minSize: 125,
      enableSorting: false,
      enableResizing: false,
      cell: ({ cell, row }) => (
        <div className='d-flex gap-1'>
          <TooltipButton title={`Edit User`}>
            <button className='btn btn-outline-primary py-1 px-2 border-0' onClick={(e) => {
              e.stopPropagation();
              setSelectedUser(row.original);
            }}>
              <BiEdit size={20} />
            </button>
          </TooltipButton>
          {
            IsAdminUser(user) &&
            <DeleteRowButton cell={cell} {...{
              row,
              deleteAPI: () => DeleteUser(row.original.userID, user.userID).then((resp) => {
                if (resp.data != "") {
                  showToast({
                    message: "Failed to Delete User with error: " + resp.data,
                    type: "warning",
                    duration: 5000
                  })
                }
                else {
                  if (row.original.userID == user.userID) {
                    logout();
                  }
                  else
                    getRows(defaultFilter, (vals) => {
                      setAllUsers([...vals]);
                    });
                }
              }),
              nameFieldId: "userName",
              title: "User"
            }} />
          }
        </div>
      )
    }
  }

  const getRows = (request: any, afterLoad: any) => {
    setRows(null);
    GetUsers(request).then((x) => {
      setSelectedFilter(request);
      setRows([...x.data]);
      if (typeof afterLoad != 'undefined')
        afterLoad(x.data);
    })
  }

  useEffect(() => {
    getRows(defaultFilter, undefined);
  }, [])

  const getUserName = (row: any) => {
    if (typeof row.original != 'undefined')
      row = row.original;
    return (row.userFirstName == "" && row.userLastName == "") ? row.userName : row.userFirstName + " " + row.userLastName
  }

  const setSelectedUser = (u: UserModel) => {
    navigate("edit?id=" + u.userID);
  };

  return (
    <>
      <ComponentLayout exButtons={undefined} instanceId={0} objectType={AuditObjectTypes.User} title='Users available in system'
        bodyHeight={300}
        subHeader={
          <div className='d-flex flex-column w-100'>
            <SearchAndAdvancedFilters {...{
              initialValues: { "": null, isAdUser: null },
              filters: {
                "roleID": {
                  exValues: [],
                  filterType: appliedFilterType.Role,
                  icon: <FaUsersGear />,
                  label: "Role"
                },
                "roleGroupID": {
                  exValues: [],
                  filterType: appliedFilterType.RoleGroup,
                  icon: <FaSitemap />,
                  label: LabelConstants.RoleGroups
                },
                "managerUserID": {
                  exValues: [],
                  filterType: appliedFilterType.User,
                  appliedFilter: (u: UserModel) => u.applicationAccess == 1,
                  icon: <FaUserLarge />,
                  label: "Managers"
                },
                "appliationAccess": {
                  exValues: [],
                  filterType: appliedFilterType.UserAccess,
                  icon: <RiAdminLine />,
                  label: "Application Access"
                },
                "isAdUser": {
                  exValues: [],
                  filterType: appliedFilterType.UserADStatus,
                  icon: <FaIdCard />,
                  label: "Identity Source"
                }
              },
              values: {
                "Dimensions": null,
                "roleID": null,
                "roleGroupID": null,
                "managerUserID": null,
                "appliationAccess": null,
                "isAdUser": null
              },
              exButton: <div className='d-flex flex-row align-items-center gap-2 overflow-visible text-nowrap t-user-create' >
                <TooltipButton title="Sync Active Directory: Automatically pull and update the latest profiles, hierarchy, and statuses for all AD users into the local database.">
                  <button
                    className='btn btn-outline-primary shadow-sm d-flex align-items-center justify-content-center'
                    style={{ height: "35px", padding: "0 14px" }}
                    onClick={() => setIsSyncModalOpen(true)}
                  >
                    <FaSyncAlt className="me-2" size={14} />
                    <span className="fw-semibold" style={{ fontSize: "13px" }}>Sync AD</span>
                  </button>
                </TooltipButton>
                <CreateButton {...{
                  title: "Add a user from AD",
                  label: "Add a User",
                  onClick: () => {
                    setIsAdModalOpen(true);
                  }
                }} />
              </div>,
              onChange: (filters, afterLoad) => {
                getRows({
                  roleID: filters.roleID, userName: filters.name, email: filters.name, managerUserID: filters.managerUserID,
                  roleGroupID: filters.roleGroupID, userID: 0, appliationAccess: filters.appliationAccess,
                  isAdUser: filters.isAdUser ? parseInt(filters.isAdUser) : null
                }, afterLoad);
              }
            }} />
            <TableSummary {...{
              total: allUsers?.length, filterred: rows?.length, name: "User",
              applyiedFilter: (!AreObjectsEquals(selectedFilter, defaultFilter))
            }} />
          </div>
        } cBody={
          <TableLayoutEx needRowSelection={false} onRowSelection={setRowSelection} handleRowDoubleClick={(row) => {
            setSelectedUser(row.original);
          }} needSelection={true} loading={rows == null} columnDefinition={colDefinitions} expandedUI={undefined} objType={AuditObjectTypes.User} rows={(rows == null) ? [] : rows}
            isSelected={(row) => {
              return row.getIsSelected();
            }}
            setSelected={(row) => {
              row.toggleSelected();
            }} emptyMessage={
              <AnimatePresence>
                <motion.div
                  className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  style={{
                    border: "2px dashed #dee2e6",
                    borderRadius: "10px",
                    marginTop: "40px",
                  }}
                >
                  <FaUserLarge size={60} color="#adb5bd" className="mb-3" />
                  <h5 className="fw-semibold">No User Found</h5>
                  {
                    (!AreObjectsEquals(selectedFilter, defaultFilter)) ?
                      <p className="text-center" style={{ maxWidth: 400 }}>
                        No user found for selected filter.
                      </p> :
                      <p className="text-center" style={{ maxWidth: 400 }}>
                        It looks like there are no records yet. Click{" "}
                        <strong>“Create”</strong> to get started.
                      </p>
                  }
                </motion.div>
              </AnimatePresence>
            } />
        } />

      {/* Reset Password Modal Overlay */}
      <AnimatePresence>
        {resetUsers && resetUsers.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="p-4 rounded shadow-lg d-flex flex-column align-items-center text-center"
              style={{ maxWidth: '450px', width: '90%', backgroundColor: '#fff', color: '#333' }}
            >

              {/* IDLE STATE */}
              {resetStatus === 'idle' && (
                <>
                  <div className="mb-3 d-flex justify-content-center align-items-center" style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#fff3cd', color: '#ffc107' }}>
                    <FaKey size={28} />
                  </div>
                  <h4 className="fw-bold mb-3">Reset Password</h4>
                  <p className="text-muted mb-4">
                    {resetUsers.length === 1 ? (
                      <>Are you sure you want to reset the password for <strong>{resetUsers[0].userName || resetUsers[0].email}</strong>? A new secure password will be generated and dispatched to their email.</>
                    ) : (
                      <>Are you sure you want to reset passwords for the <strong>{resetUsers.length}</strong> selected users? New secure passwords will be generated and dispatched to their respective emails.</>
                    )}
                  </p>
                  <div className="d-flex w-100 gap-2">
                    <button className="btn btn-outline-secondary w-50 fw-semibold" onClick={() => setResetUsers(null)}>Cancel</button>
                    <button className="btn btn-warning w-50 fw-semibold text-white" onClick={confirmResetPassword}>Yes, Reset</button>
                  </div>
                </>
              )}

              {/* LOADING STATE */}
              {resetStatus === 'loading' && (
                <div className="py-4 d-flex flex-column align-items-center">
                  <Spinner animation="border" variant="warning" style={{ width: '3rem', height: '3rem' }} />
                  <h5 className="mt-4 fw-semibold">Generating Passwords...</h5>
                  <p className="text-muted mb-0">Please wait while secure emails are dispatched.</p>
                </div>
              )}

              {/* SUCCESS STATE */}
              {resetStatus === 'success' && (
                <>
                  <div className="mb-3 d-flex justify-content-center align-items-center" style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#d1e7dd', color: '#198754' }}>
                    <FaCheckCircle size={32} />
                  </div>
                  <h4 className="fw-bold mb-3">Reset Complete</h4>
                  <p className="text-muted mb-4">
                    Successfully sent new passwords to <strong>{resetResult?.successfulCount || 0}</strong> user(s).
                    {resetResult && resetResult.failedCount > 0 && (
                      <span className="d-block text-danger mt-2">
                        However, {resetResult.failedCount} user(s) failed. Please ensure they have valid emails.
                      </span>
                    )}
                  </p>
                  <button className="btn btn-success w-100 fw-semibold" onClick={() => setResetUsers(null)}>Done</button>
                </>
              )}

              {/* ERROR STATE */}
              {resetStatus === 'error' && (
                <>
                  <div className="mb-3 d-flex justify-content-center align-items-center" style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#f8d7da', color: '#dc3545' }}>
                    <FaExclamationTriangle size={32} />
                  </div>
                  <h4 className="fw-bold mb-3">Reset Failed</h4>
                  <p className="text-muted mb-4">An unexpected error occurred while attempting to generate and dispatch the new passwords. Please check your system logs and try again.</p>
                  <div className="d-flex w-100 gap-2">
                    <button className="btn btn-outline-secondary w-50 fw-semibold" onClick={() => setResetUsers(null)}>Cancel</button>
                    <button className="btn btn-danger w-50 fw-semibold" onClick={confirmResetPassword}>Retry</button>
                  </div>
                </>
              )}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AddAdUsersModal
        isOpen={isAdModalOpen}
        onClose={() => setIsAdModalOpen(false)}
        onSuccess={() => {
          // Refresh the table after successful import
          getRows(selectedFilter, (vals: any) => {
            setAllUsers([...vals]);
          });
        }}
      />

      <SyncAdUsersModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        // Filter `allUsers` context to only include AD imported users
        localAdUsers={allUsers || []}
        onSuccess={() => {
          // Refresh the grid state on successful sync
          getRows(selectedFilter, (vals: any) => {
            setAllUsers([...vals]);
          });
        }}
      />
    </>
  );
}
